import React from 'react';
import { Card, Row, Col, Tag, Typography, Badge, Tooltip, Spin, Empty, Button } from 'antd';
import { MailOutlined, TwitterOutlined, MessageOutlined, LinkOutlined, StarFilled, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { kanbanApi } from '../api/client';

const { Title, Text } = Typography;

interface KanbanProject {
  id: string;
  name: string;
  website?: string;
  priority_score?: number;
  email?: string;
  twitter_handle?: string;
  telegram_group?: string;
  last_outreach_at?: string;
  next_outreach_at?: string;
}

interface KanbanColumn {
  key: string;
  title: string;
  count: number;
  projects: KanbanProject[];
}

const getPriorityColor = (score?: number): string => {
  if (score == null) return '#d9d9d9';
  if (score >= 80) return '#f5222d';
  if (score >= 60) return '#fa8c16';
  if (score >= 40) return '#fadb14';
  return '#52c41a';
};

const styles = {
  container: {
    padding: 24,
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  } as React.CSSProperties,
  columnsWrapper: {
    display: 'flex',
    gap: 16,
    overflowX: 'auto' as const,
    paddingBottom: 16,
  } as React.CSSProperties,
  column: {
    minWidth: 250,
    maxWidth: 250,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: 'calc(100vh - 180px)',
  } as React.CSSProperties,
  columnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '2px solid #e8e8e8',
  } as React.CSSProperties,
  columnBody: {
    flex: 1,
    overflowY: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  } as React.CSSProperties,
  card: {
    cursor: 'pointer',
    borderRadius: 6,
  } as React.CSSProperties,
  cardName: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  } as React.CSSProperties,
  icons: {
    display: 'flex',
    gap: 6,
  } as React.CSSProperties,
  spinWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  } as React.CSSProperties,
};

const ProjectCard: React.FC<{ project: KanbanProject }> = ({ project }) => {
  const navigate = useNavigate();

  return (
    <Card
      size="small"
      style={styles.card}
      hoverable
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      <div style={styles.cardName}>
        <Tooltip title={project.name}>{project.name}</Tooltip>
      </div>

      {project.website && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          <LinkOutlined style={{ marginRight: 4 }} />
          {project.website.replace(/^https?:\/\//, '').slice(0, 28)}
        </Text>
      )}

      <div style={styles.cardFooter}>
        <div style={styles.icons}>
          {project.email && (
            <Tooltip title={project.email}>
              <MailOutlined style={{ color: '#1890ff' }} />
            </Tooltip>
          )}
          {project.twitter_handle && (
            <Tooltip title={`@${project.twitter_handle}`}>
              <TwitterOutlined style={{ color: '#1da1f2' }} />
            </Tooltip>
          )}
          {project.telegram_group && (
            <Tooltip title={project.telegram_group}>
              <MessageOutlined style={{ color: '#0088cc' }} />
            </Tooltip>
          )}
        </div>

        {project.priority_score != null && (
          <Tooltip title={`Priority: ${project.priority_score}`}>
            <Tag
              color={getPriorityColor(project.priority_score)}
              style={{ margin: 0, fontSize: 11 }}
            >
              <StarFilled style={{ marginRight: 2 }} />
              {project.priority_score}
            </Tag>
          </Tooltip>
        )}
      </div>

      {project.next_outreach_at && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
          Next: {new Date(project.next_outreach_at).toLocaleDateString()}
        </Text>
      )}
    </Card>
  );
};

const KanbanBoard: React.FC = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['kanban'],
    queryFn: kanbanApi.getColumns,
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div style={styles.spinWrapper}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={styles.container}>
        <Empty description="Failed to load Kanban data">
          <Button onClick={() => refetch()}>Retry</Button>
        </Empty>
      </div>
    );
  }

  const columns: KanbanColumn[] = data.columns;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Title level={3} style={{ margin: 0 }}>
          项目看板
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </div>

      <div style={styles.columnsWrapper}>
        {columns.map((col) => (
          <div key={col.key} style={styles.column}>
            <div style={styles.columnHeader}>
              <Text strong style={{ fontSize: 15 }}>{col.title}</Text>
              <Badge
                count={col.count}
                style={{ backgroundColor: '#1890ff' }}
                overflowCount={999}
              />
            </div>
            <div style={styles.columnBody}>
              {col.projects.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No projects"
                  style={{ margin: '20px 0' }}
                />
              ) : (
                col.projects.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KanbanBoard;
